import { useEffect, useMemo, useState, type Key } from "react";
import { Button, Modal, Table, Tag } from "antd";
import clsx from "clsx";
import {
  JournalSectionPagination,
  useJournalSectionPagination,
} from "@/entities/journal/ui";
import { useDebounce } from "@/shared/utils";
import {
  JournalSectionFirstFilter,
  JournalSectionFirstActiveFilterChips,
} from "../journals-section-first/ui/journals-section-first/journal-section-first-filter/journal-section-first-filter";
import {
  SectionFourFilters,
  SectionFourActiveFilterChips,
} from "../journals-section-fourth/ui/journals-section-fourth/ui/section-four-filters/section-four-filters";
import {
  JournalSectionSecondFilter,
  JournalSectionSecondActiveFilterChips,
} from "../journals-section-second/ui/journal-section-second-filter/journal-section-second-filter";
import {
  SectionThirdFilters,
  SectionThirdActiveFilterChips,
} from "../journals-section-third/ui/journal-section-form/ui/section-third-filters/section-third-filters";
import "./journal-bulk-send-to-sign-modal.scss";
import {
  BULK_SEND_MAX_SELECTION_SECTION_FOUR,
  getBulkSendListTotal,
  getBulkSendPartId,
  getBulkSendSequenceNumber,
  useBulkSendListQuery,
  type BulkSendItemPayload,
  type JournalBulkSendToSignModalProps,
} from "./journal-bulk-send-to-sign-modal.utils";

export type {
  BulkSendItemPayload,
  JournalBulkSendSection,
  JournalBulkSendToSignModalProps,
} from "./journal-bulk-send-to-sign-modal.utils";

export const JournalBulkSendToSignModal = ({
  open,
  onClose,
  section,
  signersList = [],
  isSending = false,
  onSendBatch,
  sectionOneId,
  sectionTwoId,
  sectionTwoListId,
  sectionThreeListId,
  sectionFourListId,
  resolveTable,
}: JournalBulkSendToSignModalProps) => {
  const pagination = useJournalSectionPagination(10);
  const { page, setPage, limit } = pagination;

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);

  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [signerIds, setSignerIds] = useState<string[]>([]);
  const [specialJournalDate, setSpecialJournalDate] = useState("");
  const [dateHandover, setDateHandover] = useState("");
  const [workTypes, setWorkTypes] = useState<string[]>([]);
  const [actConnect, setActConnect] = useState(false);
  const [defectDeadline, setDefectDeadline] = useState("");
  const [eliminationDate, setEliminationDate] = useState("");

  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<string[]>([]);
  const [selectedEntriesMap, setSelectedEntriesMap] = useState<
    Record<string, BulkSendItemPayload>
  >({});

  const resetModalState = () => {
    setSearch("");
    setDateStart("");
    setDateEnd("");
    setSignerIds([]);
    setSpecialJournalDate("");
    setDateHandover("");
    setWorkTypes([]);
    setActConnect(false);
    setDefectDeadline("");
    setEliminationDate("");
    setSelectedRowKeys([]);
    setSelectedOrder([]);
    setSelectedEntriesMap({});
    setPage(1);
  };

  useEffect(() => {
    if (!open) {
      resetModalState();
    }
  }, [open]);

  const { data, isFetching } = useBulkSendListQuery({
    section,
    enabled: open,
    page,
    limit,
    search: debouncedSearch,
    sectionOneId,
    sectionTwoId,
    sectionTwoListId,
    sectionThreeListId,
    sectionFourListId,
    dateStart,
    dateEnd,
    signerIds,
    specialJournalDate,
    dateHandover,
    workTypes,
    actConnect,
    defectDeadline,
    eliminationDate,
  });

  const tableView = useMemo(
    () => (data ? resolveTable(data) : { columns: [], dataSource: [], rowKey: "key" }),
    [data, resolveTable],
  );

  const rowKey = tableView.rowKey ?? "key";
  const rows = useMemo(
    () =>
      tableView.dataSource.filter((row) => {
        if (section === 2) {
          return row.status === "NEW";
        }

        if (section === 4) {
          const itemInfo = row.itemInfo as
            | {
                status?: string;
                itemPartOne?: { status?: string };
              }
            | undefined;
          const status =
            itemInfo?.itemPartOne?.status ?? itemInfo?.status ?? row.status;
          return status === "NEW";
        }

        return true;
      }),
    [section, tableView.dataSource],
  );
  const total = getBulkSendListTotal(data);

  const maxSelection =
    section === 4 ? BULK_SEND_MAX_SELECTION_SECTION_FOUR : undefined;

  const rowsByKey = useMemo(() => {
    const map = new Map<string, Record<string, unknown>>();
    rows.forEach((row) => {
      const id = row[rowKey];
      if (id != null) {
        map.set(String(id), row);
      }
    });
    return map;
  }, [rows, rowKey]);

  const syncSelectionOrder = (keys: Key[]) => {
    const keyStrings = keys.map(String);
    const limitedKeys =
      maxSelection !== undefined
        ? keyStrings.slice(0, maxSelection)
        : keyStrings;

    setSelectedOrder((prev) => {
      const keySet = new Set(limitedKeys);
      const kept = prev.filter((id) => keySet.has(id));
      const keptSet = new Set(kept);
      const added = limitedKeys.filter((id) => !keptSet.has(id));
      return [...kept, ...added];
    });

    setSelectedRowKeys(limitedKeys);
    setSelectedEntriesMap((prev) => {
      const next: Record<string, BulkSendItemPayload> = {};
      limitedKeys.forEach((id) => {
        const row = rowsByKey.get(id);
        if (row) {
          next[id] = {
            itemNoVersionId: id,
            selected: true,
            partId: getBulkSendPartId(section, row),
            sequenceNumber: getBulkSendSequenceNumber(section, row),
          };
        } else if (prev[id]) {
          next[id] = prev[id];
        } else {
          next[id] = { itemNoVersionId: id, selected: true };
        }
      });
      return next;
    });
  };

  const handleFiltersApply = () => {
    setPage(1);
  };

  const handleSend = async () => {
    const items = selectedOrder.map(
      (id) =>
        selectedEntriesMap[id] ?? {
          itemNoVersionId: id,
          selected: true as const,
        },
    );
    if (items.length === 0) return;
    await onSendBatch(items);
  };

  const removeSelected = (id: string) => {
    syncSelectionOrder(selectedOrder.filter((item) => item !== id));
  };

  const clearSelection = () => {
    syncSelectionOrder([]);
  };

  const getSelectionTagLabel = (id: string) => {
    const row = rowsByKey.get(id);
    const sequenceNumber = row
      ? getBulkSendSequenceNumber(section, row)
      : selectedEntriesMap[id]?.sequenceNumber;
    return sequenceNumber != null ? `№ ${sequenceNumber}` : id;
  };

  const renderFilters = () => {
    switch (section) {
      case 1:
        return (
          <>
            <JournalSectionFirstFilter
              bulkSendMode
              setSearch={setSearch}
              setDateStart={setDateStart}
              setDateEnd={setDateEnd}
              dateStart={dateStart}
              dateEnd={dateEnd}
              selectedStatus={null}
              setSelectedStatus={() => {}}
              signersList={signersList as never[]}
              onFiltersApply={handleFiltersApply}
            />
            <JournalSectionFirstActiveFilterChips
              bulkSendMode
              selectedStatus={null}
              setSelectedStatus={() => {}}
              dateStart={dateStart}
              dateEnd={dateEnd}
              setDateStart={setDateStart}
              setDateEnd={setDateEnd}
              onFilterRemove={handleFiltersApply}
            />
          </>
        );
      case 2:
        return (
          <>
            <JournalSectionSecondFilter
              bulkSendMode
              onSearchChange={setSearch}
              selectedStatuses={[]}
              setSelectedStatuses={() => {}}
              signerIds={signerIds}
              setSignerIds={setSignerIds}
              specialJournalDate={specialJournalDate}
              setSpecialJournalDate={setSpecialJournalDate}
              dateHandover={dateHandover}
              setDateHandover={setDateHandover}
              signersList={signersList as never[]}
              onFiltersApply={handleFiltersApply}
            />
            <JournalSectionSecondActiveFilterChips
              bulkSendMode
              selectedStatuses={[]}
              setSelectedStatuses={() => {}}
              signerIds={signerIds}
              setSignerIds={setSignerIds}
              specialJournalDate={specialJournalDate}
              setSpecialJournalDate={setSpecialJournalDate}
              dateHandover={dateHandover}
              setDateHandover={setDateHandover}
              signersList={signersList as never[]}
              onFilterRemove={handleFiltersApply}
            />
          </>
        );
      case 3:
        return (
          <>
            <SectionThirdFilters
              bulkSendMode
              onSearchChange={setSearch}
              selectedStatuses={[]}
              setSelectedStatuses={() => {}}
              workTypes={workTypes}
              setWorkTypes={setWorkTypes}
              signerIds={signerIds}
              setSignerIds={setSignerIds}
              actConnect={actConnect}
              setActConnect={setActConnect}
              createDate=""
              setCreateDate={() => {}}
              updateDate=""
              setUpdateDate={() => {}}
              dateStart={dateStart}
              dateEnd={dateEnd}
              setDateStart={setDateStart}
              setDateEnd={setDateEnd}
              signersList={signersList as never[]}
              onFiltersApply={handleFiltersApply}
            />
            <SectionThirdActiveFilterChips
              bulkSendMode
              selectedStatuses={[]}
              setSelectedStatuses={() => {}}
              workTypes={workTypes}
              setWorkTypes={setWorkTypes}
              signerIds={signerIds}
              setSignerIds={setSignerIds}
              actConnect={actConnect}
              setActConnect={setActConnect}
              dateStart={dateStart}
              dateEnd={dateEnd}
              setDateStart={setDateStart}
              setDateEnd={setDateEnd}
              signersList={signersList as never[]}
              onFilterRemove={handleFiltersApply}
            />
          </>
        );
      case 4:
        return (
          <>
            <SectionFourFilters
              bulkSendMode
              onSearchChange={setSearch}
              selectedStatuses={[]}
              setSelectedStatuses={() => {}}
              signerIds={signerIds}
              setSignerIds={setSignerIds}
              controlPeriodStart=""
              controlPeriodEnd=""
              setControlPeriodStart={() => {}}
              setControlPeriodEnd={() => {}}
              defectDeadline={defectDeadline}
              setDefectDeadline={setDefectDeadline}
              eliminationDate={eliminationDate}
              setEliminationDate={setEliminationDate}
              createDate=""
              setCreateDate={() => {}}
              updateDate=""
              setUpdateDate={() => {}}
              defectsFilter=""
              setDefectsFilter={() => {}}
              signersList={signersList as never[]}
              onFiltersApply={handleFiltersApply}
            />
            <SectionFourActiveFilterChips
              bulkSendMode
              selectedStatuses={[]}
              setSelectedStatuses={() => {}}
              signerIds={signerIds}
              setSignerIds={setSignerIds}
              defectDeadline={defectDeadline}
              setDefectDeadline={setDefectDeadline}
              eliminationDate={eliminationDate}
              setEliminationDate={setEliminationDate}
              signersList={signersList as never[]}
              onFilterRemove={handleFiltersApply}
            />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      className="journal-bulk-send-to-sign-modal"
      title="Отправка записей на подписание"
      open={open}
      onCancel={onClose}
      width="min(96vw, 1400px)"
      destroyOnClose
      footer={null}
    >
      <div className="journal-bulk-send-to-sign-modal__body">
        <div className="journal-bulk-send-to-sign-modal__filters">
          {renderFilters()}
        </div>

        <div className="journal-bulk-send-to-sign-modal__table-wrap">
          <Table
            rowKey={rowKey}
            loading={isFetching}
            className={clsx(tableView.tableClassName)}
            columns={tableView.columns}
            dataSource={rows}
            pagination={false}
            scroll={{ x: true }}
            rowSelection={{
              selectedRowKeys,
              preserveSelectedRowKeys: true,
              onChange: syncSelectionOrder,
              getCheckboxProps: (record) => {
                const id = String(record[rowKey] ?? "");
                return {
                  disabled:
                    maxSelection !== undefined &&
                    selectedOrder.length >= maxSelection &&
                    !selectedOrder.includes(id),
                };
              },
            }}
          />
          <JournalSectionPagination pagination={pagination} total={total} />
        </div>

        <div className="journal-bulk-send-to-sign-modal__selection-bar">
          <div className="journal-bulk-send-to-sign-modal__selection-main">
            <span className="journal-bulk-send-to-sign-modal__selection-count">
              Выбрано записей: {selectedOrder.length}
            </span>
            {selectedOrder.length > 0 ? (
              <div className="journal-bulk-send-to-sign-modal__selection-tags">
                {selectedOrder.map((id) => (
                  <Tag
                    key={id}
                    closable
                    onClose={(event) => {
                      event.preventDefault();
                      removeSelected(id);
                    }}
                  >
                    {getSelectionTagLabel(id)}
                  </Tag>
                ))}
              </div>
            ) : null}
          </div>
          {section === 4 ? (
            <span className="journal-bulk-send-to-sign-modal__selection-limit">
              Максимум единовременно: {BULK_SEND_MAX_SELECTION_SECTION_FOUR}
            </span>
          ) : null}
        </div>

        <div className="journal-bulk-send-to-sign-modal__actions">
          <Button
            type="primary"
            disabled={selectedOrder.length === 0}
            loading={isSending}
            onClick={handleSend}
          >
            Отправить выбранное на подпись
          </Button>
          <Button disabled={selectedOrder.length === 0} onClick={clearSelection}>
            Отменить выбор
          </Button>
        </div>
      </div>
    </Modal>
  );
};
